import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../api/api";
import MonthlyPerformance from "../../components/MonthlyPerformance";
import { fmt2, fmtPct, clrPnl, clrDD, DirBadge, PnlCell, StatCard, THead, trHover, Pagination, EmptyState, SectionLabel } from "../../components/ui/shared";

const BASE = import.meta.env.VITE_API_BASE_URL || "https://crunchalpha.com/api";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` });

// ── Plan Equity Curve ────────────────────────────────────────────────────────
const PlanEquityCurve = ({ trades }) => {
  if (!trades || trades.length === 0) return null;

  const sorted = [...trades].sort((a, b) => new Date(a.closeTime) - new Date(b.closeTime));
  const W = 700, H = 200, PAD = { t: 16, r: 16, b: 32, l: 52 };
  const iW = W - PAD.l - PAD.r;
  const iH = H - PAD.t - PAD.b;

  const equities = sorted.map(t => t.runningEquity || 0);
  const maxEq = Math.max(...equities, 0.01);
  const mcIndex = sorted.findIndex(t => t.drawdownPct >= 100);

  const xScale = i => PAD.l + (i / (sorted.length - 1)) * iW;
  const yScale = v => PAD.t + iH - (v / maxEq) * iH;

  const points = sorted.map((t, i) => `${xScale(i)},${yScale(t.runningEquity || 0)}`).join(" ");

  // Y axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => ({
    y: PAD.t + iH - p * iH,
    label: "$" + fmt2(p * maxEq)
  }));

  return (
    <div style={{ background: "var(--bg-surface)", border: "0.5px solid var(--border)", borderRadius: 10, padding: "16px 12px", marginBottom: 20 }}>
      <SectionLabel>Plan Equity Curve</SectionLabel>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
        {/* Grid */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={t.y} x2={W - PAD.r} y2={t.y} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
            <text x={PAD.l - 6} y={t.y + 4} textAnchor="end" fontSize={9} fill="rgba(255,255,255,0.3)">{t.label}</text>
          </g>
        ))}

        {/* Equity line */}
        <polyline points={points} fill="none" stroke="#4e9af1" strokeWidth={1.5} />

        {/* MC vertical line */}
        {mcIndex >= 0 && (
          <g>
            <line
              x1={xScale(mcIndex)} y1={PAD.t}
              x2={xScale(mcIndex)} y2={PAD.t + iH}
              stroke="rgba(239,68,68,0.6)" strokeWidth={1.5} strokeDasharray="4,3"
            />
            <text x={xScale(mcIndex) + 4} y={PAD.t + 12} fontSize={9} fill="rgba(239,68,68,0.8)" fontWeight={700}>
              Blown
            </text>
          </g>
        )}

        {/* Dots on each trade */}
        {sorted.map((t, i) => (
          <circle key={i}
            cx={xScale(i)} cy={yScale(t.runningEquity || 0)}
            r={2.5}
            fill={t.planResult === "TP" ? "rgba(34,197,94,0.8)" : "rgba(239,68,68,0.8)"}
          />
        ))}

        {/* X axis */}
        <line x1={PAD.l} y1={PAD.t + iH} x2={W - PAD.r} y2={PAD.t + iH} stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
        <text x={PAD.l} y={H - 4} fontSize={9} fill="rgba(255,255,255,0.3)">Trade #1</text>
        <text x={W - PAD.r} y={H - 4} textAnchor="end" fontSize={9} fill="rgba(255,255,255,0.3)">#{sorted.length}</text>
      </svg>
      <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 10, color: "var(--text-muted)" }}>
        <span><span style={{ color: "rgba(34,197,94,0.9)", fontWeight: 700 }}>●</span> TP hit</span>
        <span><span style={{ color: "rgba(239,68,68,0.9)", fontWeight: 700 }}>●</span> SL hit</span>
        {mcIndex >= 0 && <span><span style={{ color: "rgba(239,68,68,0.6)", fontWeight: 700 }}>- -</span> Account Blown</span>}
      </div>
    </div>
  );
};

// ── Result Badge ─────────────────────────────────────────────────────────────
const ResultBadge = ({ result, status }) => {
  if (status === "NO_SLTP") return <span style={{ fontSize: 10, color: "var(--text-muted)" }}>No SL/TP</span>;
  if (status === "PENDING") return <span style={{ fontSize: 10, color: "var(--warning)" }}>Pending</span>;
  if (result === "TP") return <span style={{ fontSize: 11, fontWeight: 800, color: "var(--success)" }}>TP </span>;
  if (result === "SL") return <span style={{ fontSize: 11, fontWeight: 800, color: "var(--danger)" }}>SL </span>;
  return <span style={{ fontSize: 10, color: "var(--text-muted)" }}>—</span>;
};

// ── Main Component ────────────────────────────────────────────────────────────
const TradeHistoryPage = () => {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("account_id");

  const [account, setAccount]   = useState(null);
  const [mode, setMode]         = useState("LIVE"); // "LIVE" | "PLAN"
  const [liveTrades, setLiveTrades] = useState([]);
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const PER_PAGE = 50;

  useEffect(() => {
    if (!accountId) { setLoading(false); return; }
    const fetchAll = async () => {
      try {
        const [accountsData, tradesData] = await Promise.all([
          api.getAccounts(),
          api.getTrades(accountId, 500)
        ]);
        setAccount(accountsData.find(a => (a.ID || a.id) === accountId));
        setLiveTrades(tradesData.trades || []);

        // Fetch plan data
        const r = await fetch(`${BASE}/trader/trades-plan?account_id=${accountId}&limit=500`, { headers: auth() });
        if (r.ok) setPlanData(await r.json());
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [accountId]);

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>Loading...</div>;
  if (!accountId || !account) return (
    <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
      <p>No account selected.</p>
      <Link to="/trader" style={{ color: "var(--accent)" }}>Back to Dashboard</Link>
    </div>
  );

  const accountNumber = account.AccountNumber || account.account_number || "N/A";
  const isLive = mode === "LIVE";

  // ── LIVE summary ──
  const liveTotal  = liveTrades.length;
  const liveWins   = liveTrades.filter(t => t.profit > 0).length;
  const liveLosses = liveTrades.filter(t => t.profit < 0).length;
  const liveWR     = liveTotal > 0 ? liveWins / liveTotal * 100 : 0;
  const liveNetPL  = liveTrades.reduce((s, t) => s + t.profit, 0);

  // ── PLAN summary ──
  const ps         = planData?.plan_summary || {};
  const planTrades = planData?.trades || [];
  const mcIndex    = planTrades.findIndex(t => t.drawdownPct >= 100);

  // Pagination
  const activeTrades = isLive ? liveTrades : planTrades;
  const totalPages   = Math.max(1, Math.ceil(activeTrades.length / PER_PAGE));
  const paginated    = activeTrades.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div style={{ padding: "32px 0", maxWidth: 1200, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link to={`/trader?account_id=${accountId}`}
          style={{ display: "inline-block", marginBottom: 12, color: "var(--accent)", textDecoration: "none", fontSize: 13, fontWeight: 600 }}>
          ← Back to Dashboard
        </Link>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, margin: "0 0 4px 0", color: "var(--text-main)" }}>Trade History</h1>
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Account #{accountNumber}</p>
          </div>
          {/* Toggle LIVE / PLAN */}
          <div style={{ display: "flex", background: "var(--bg-surface)", border: "0.5px solid var(--border)", borderRadius: 8, padding: 3, gap: 3 }}>
            {["LIVE", "PLAN"].map(m => (
              <button key={m} onClick={() => { setMode(m); setPage(1); }}
                style={{
                  padding: "6px 20px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                  background: mode === m ? "var(--accent)" : "transparent",
                  color: mode === m ? "#fff" : "var(--text-muted)",
                  transition: "all 0.15s"
                }}>
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
        {isLive ? <>
          <StatCard center={true} label="Total Trades" value={liveTotal} />
          <StatCard center={true} label="Wins" value={liveWins} color="var(--success)" />
          <StatCard center={true} label="Losses" value={liveLosses} color="var(--danger)" />
          <StatCard center={true} label="Win Rate" value={fmtPct(liveWR)} />
          <StatCard center={true} label="Net P&L" value={(liveNetPL >= 0 ? "+$" : "-$") + fmt2(Math.abs(liveNetPL))} color={clrPnl(liveNetPL)} />
        </> : <>
          <StatCard center={true} label="Analyzed" value={ps.total || 0} />
          <StatCard center={true} label="Wins" value={ps.wins || 0} color="var(--success)" />
          <StatCard center={true} label="Losses" value={ps.losses || 0} color="var(--danger)" />
          <StatCard center={true} label="Win Rate" value={fmtPct(ps.win_rate || 0)} />
          <StatCard center={true} label="Plan ROI" value={fmtPct(ps.roi || 0)} color={clrPnl(ps.roi)} />
          <StatCard center={true} label="Max DD" value={fmtPct(ps.max_dd || 0)} color={clrDD(ps.max_dd)} />
        </>}
      </div>

      {/* Plan Equity Curve — hanya di PLAN mode */}
      {!isLive && planTrades.length > 0 && (
        <PlanEquityCurve trades={[...planTrades].sort((a,b) => new Date(a.closeTime)-new Date(b.closeTime))} />
      )}

      {/* Monthly Performance — hanya di LIVE mode */}
      {isLive && <MonthlyPerformance accountId={accountId} />}

      {/* MC Warning Banner */}
      {!isLive && ps.max_dd >= 100 && (
        <div style={{
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: 8, padding: "10px 16px", marginBottom: 16,
          display: "flex", alignItems: "center", gap: 10
        }}>
          <span style={{ color: "var(--danger)", fontSize: 14 }}>  </span>
          <span style={{ fontSize: 12, color: "var(--danger)", fontWeight: 600 }}>
            Plan simulation reached 100% drawdown at trade #{mcIndex + 1} — account would have been blown following this plan strictly.
          </span>
        </div>
      )}

      {/* Trade Table */}
      <div style={{ background: "var(--bg-surface)", border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <SectionLabel count={activeTrades.length}>
            {isLive ? "Closed Trades" : "Plan Trades"}
          </SectionLabel>
        </div>

        {activeTrades.length === 0
          ? <EmptyState title="No trades yet" subtitle="Trades will appear after EA integration is complete." />
          : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <THead cols={isLive
                  ? ["Symbol", "Dir", "Lots", "Entry", "Close", "SL", "TP", "P&L", "Date"]
                  : ["Symbol", "Dir", "Lots", "Entry", "Plan Exit", "Result", "Live P&L", "Plan P&L", "Date"]
                } />
                <tbody>
                  {paginated.map((t, i) => {
                    const isMC = !isLive && t.drawdownPct >= 100 && t.runningEquity === 0;
                    return (
                      <tr key={t.ticket || i} style={{
                        ...trHover(i),
                        opacity: isMC ? 0.45 : 1,
                        background: isMC ? "rgba(239,68,68,0.03)" : undefined
                      }}>
                        <td style={{ padding: "9px 12px", fontWeight: 600, color: "var(--text-main)" }}>{t.symbol}</td>
                        <td style={{ padding: "9px 12px" }}><DirBadge dir={t.type} /></td>
                        <td style={{ padding: "9px 12px", color: "var(--text-muted)" }}>{Number(t.lots || 0).toFixed(2)}</td>
                        <td style={{ padding: "9px 12px", color: "var(--text-muted)" }}>{fmt2(t.openPrice)}</td>

                        {isLive ? <>
                          <td style={{ padding: "9px 12px", color: "var(--text-muted)" }}>{fmt2(t.closePrice)}</td>
                          <td style={{ padding: "9px 12px", color: "var(--text-muted)" }}>{t.sl ? fmt2(t.sl) : "—"}</td>
                          <td style={{ padding: "9px 12px", color: "var(--text-muted)" }}>{t.tp ? fmt2(t.tp) : "—"}</td>
                          <td style={{ padding: "9px 12px" }}><PnlCell value={t.profit} /></td>
                        </> : <>
                          <td style={{ padding: "9px 12px", color: "var(--text-muted)" }}>
                            {t.planResult === "TP" ? fmt2(t.tp) : t.planResult === "SL" ? fmt2(t.sl) : "—"}
                          </td>
                          <td style={{ padding: "9px 12px" }}><ResultBadge result={t.planResult} status={t.planStatus} /></td>
                          <td style={{ padding: "9px 12px" }}><PnlCell value={t.profit} /></td>
                          <td style={{ padding: "9px 12px" }}>
                            {t.planStatus === "RESOLVED"
                              ? <PnlCell value={t.planPnl} />
                              : <span style={{ color: "var(--text-muted)", fontSize: 11 }}>—</span>}
                          </td>
                        </>}

                        <td style={{ padding: "9px 12px", color: "var(--text-muted)", fontSize: 11, whiteSpace: "nowrap" }}>
                          {t.closeTime ? new Date(t.closeTime).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div style={{ padding: "0 16px 12px" }}>
                  <Pagination page={page} totalPages={totalPages} setPage={setPage} />
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default TradeHistoryPage;
